import type { ReactNode } from 'react'

export interface SegmentedRowOption<T extends string> {
  value: T
  label: string
}

export interface SegmentedRowProps<T extends string> {
  label: string
  detail?: ReactNode
  options: ReadonlyArray<SegmentedRowOption<T>>
  value: T
  onChange: (next: T) => void
  disabledOptions?: ReadonlyArray<T>
  'data-testid'?: string
}

/**
 * Layout primitive: label + optional detail on the left, compact segmented
 * toggle on the right. Generic over the option value type `T`.
 *
 * Composed internally rather than delegating to `<Segmented>` because the
 * visual language differs: `Segmented` is a roomy pill/tab-bar style
 * (surface-2 container, 13px body text) while this uses compact monospace
 * buttons with an ink fill on selection — matching `HalfDayRow`'s SegBtn
 * treatment. Mixing both would create a visual inconsistency.
 */
export function SegmentedRow<T extends string>({
  label,
  detail,
  options,
  value,
  onChange,
  disabledOptions,
  'data-testid': testId,
}: SegmentedRowProps<T>): JSX.Element {
  return (
    <div
      data-testid={testId ?? 'segmented-row'}
      style={{
        padding: '8px 10px',
        border: '1px solid var(--border)',
        borderRadius: 8,
        background: 'var(--surface)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--text-3)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          {label}
        </span>
        {detail != null && (
          <span
            style={{
              fontFamily: 'var(--font-mono, inherit)',
              fontSize: 12,
              color: 'var(--text)',
            }}
          >
            {detail}
          </span>
        )}
      </div>

      <div
        role="group"
        aria-label={label}
        style={{ display: 'flex', gap: 4 }}
      >
        {options.map((option) => {
          const active = option.value === value
          const disabled = disabledOptions?.includes(option.value) ?? false
          return (
            <SegmentedRowButton
              key={option.value}
              active={active}
              disabled={disabled}
              onClick={() => {
                if (!disabled) onChange(option.value)
              }}
            >
              {option.label}
            </SegmentedRowButton>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Internal button — kept private to this module
// ---------------------------------------------------------------------------

interface SegmentedRowButtonProps {
  active: boolean
  disabled: boolean
  onClick: () => void
  children: string
}

function SegmentedRowButton({
  active,
  disabled,
  onClick,
  children,
}: SegmentedRowButtonProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-active={active ? 'true' : 'false'}
      aria-pressed={active}
      style={{
        fontFamily: 'var(--font-mono, inherit)',
        fontSize: 10,
        padding: '4px 8px',
        borderRadius: 6,
        border: '1px solid var(--border)',
        background: active ? 'var(--text)' : 'var(--surface)',
        color: active ? 'var(--surface)' : disabled ? 'var(--border-strong)' : 'var(--text-2)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        /* 44 px minimum touch target via min-height */
        minHeight: 44,
        opacity: disabled ? 0.45 : 1,
      }}
    >
      {children}
    </button>
  )
}
